import { HttpInterceptorFn, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError, tap } from 'rxjs';
import { AdminAuthService } from '../../shared/services/admin-auth/admin-auth.service';
import { ToastService } from '../../shared/services/toast/toast.service';

export const adminAuthInterceptor: HttpInterceptorFn = (req, next) => {
  const adminAuthService = inject(AdminAuthService);
  const router = inject(Router);
  const toastService = inject(ToastService);
  const token = adminAuthService.getToken();

  // Check if this is an admin route or an endpoint that requires admin authentication
  const isAdminRoute = req.url.includes('/api/v1/admin/');
  const isInvoiceDownload = req.url.includes('/payment/invoices/download/');
  const requiresAdminAuth = isAdminRoute || isInvoiceDownload;

  // Debug logging for invoice download requests
  if (isInvoiceDownload) {
    console.log('[AdminAuthInterceptor] Invoice download request detected:', {
      url: req.url,
      hasToken: !!token,
      requiresAdminAuth,
    });
    
    if (!token) {
      console.warn('[AdminAuthInterceptor] No admin token found for invoice download request');
      console.log('[AdminAuthInterceptor] Request headers:', req.headers.keys());
    }
  }

  // Add admin token for admin API routes and invoice download endpoints
  if (token && requiresAdminAuth) {
    console.log('[AdminAuthInterceptor] Adding admin token to request:', {
      url: req.url,
      isAdminRoute,
      isInvoiceDownload,
    });
    const authReq = req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
    return next(authReq).pipe(
      tap((response) => {
        // Check for new token in response header
        if (response instanceof HttpResponse) {
          const newToken = response.headers.get('X-New-Access-Token');
          if (newToken) {
            adminAuthService.updateToken(newToken);
          }
        }
      }),
      catchError((error: HttpErrorResponse) => {
        // Handle 401 (Unauthorized) or 403 (Forbidden) responses
        if (error.status === 401 || error.status === 403) {
          // Clear invalid/expired token (silent logout - no toast from logout method)
          adminAuthService.logout(true);

          // Show toast notification
          toastService.error('Your session has expired. Please login again.', 'Session Expired');

          // Only redirect if we're not already on the login page
          const currentUrl = router.url;
          if (!currentUrl.includes('/admin/login')) {
            router.navigate(['/admin/login'], {
              queryParams: {
                returnUrl: currentUrl,
                error: 'session_expired',
                message: 'Your session has expired. Please login again.',
              },
              replaceUrl: true,
            });
          }
        }
        return throwError(() => error);
      }),
    );
  }

  return next(req).pipe(
    tap((response) => {
      // Check for new token even if no token was sent (for initial requests)
      if (response instanceof HttpResponse) {
        const newToken = response.headers.get('X-New-Access-Token');
        if (newToken) {
          adminAuthService.updateToken(newToken);
        }
      }
    }),
  );
};
